/**
 * Duplicates review modal (followups §2.1) — exact-duplicate sets, most
 * wasteful first, with checkbox selection and bulk move-to-Bin.
 *
 * Content addressing makes duplicates EXACT (same content_hash = byte-
 * identical), which is a capability Drive-class products don't ship.
 * Each set renders like the drill-down's folder groups: an expandable
 * header (first copy's name + copies × size + wasted) over one row per
 * copy showing its folder path and date, so "which copy do I keep" is an
 * informed choice. Deleting is the standard permission-checked
 * move-to-Bin; under counted-storage semantics a deleted copy stops
 * counting once the Bin drains, even though the shared blob remains.
 *
 * Gate mirrors the drill-down: owner/admin (names cross folder-permission
 * boundaries); a plain member's 403 renders as copy.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, useConfirm } from '@papan/core-ui/ui'
import { filesApi } from '@papan/files/client'
import { storageApi, type DuplicatesResponse } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { Checkbox, shortenPath, fmtDate, folderLabel } from './shared'
import { pinnedBatch } from '@papan/api-client'

export function DuplicatesModal({
  tenantId,
  onClose,
  onChanged,
  onOpenFile,
}: {
  tenantId: string
  onClose: () => void
  /** Fired after successful deletions so the page refreshes its totals. */
  onChanged: () => void
  /** Shell-owned navigate-and-reveal (followups §2.4). */
  onOpenFile?: (folderId: string | null, fileId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const confirm = useConfirm()

  const [data, setData] = useState<DuplicatesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string> | null>(null)

  const loadSeq = useRef(0)
  const load = useCallback(() => {
    const seq = ++loadSeq.current
    setLoading(true)
    storageApi.listDuplicates(tenantId)
      .then((d) => {
        if (seq !== loadSeq.current) return
        setData(d)
        setError(null)
        setForbidden(false)
      })
      .catch((e: any) => {
        if (seq !== loadSeq.current) return
        if (e?.status === 403) setForbidden(true)
        else setError(e?.message || 'Failed to load duplicates')
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false) })
  }, [tenantId])

  useEffect(() => {
    load()
    return () => { loadSeq.current++ }
  }, [load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const sets = data?.sets ?? []
  const expandedSet = expanded ?? new Set(sets.length ? [sets[0].hash] : [])
  const toggleExpanded = (hash: string) => {
    const next = new Set(expandedSet)
    if (next.has(hash)) next.delete(hash)
    else next.add(hash)
    setExpanded(next)
  }

  // #1102 item 4: a copy this person can't write (`canDelete === false`) is never selectable — the delete would only fail.
  const deletableFile = (f: { canDelete?: boolean }) => f.canDelete !== false
  const toggleFile = (id: string) => {
    if (sets.some((st) => st.files.some((f) => f.id === id && !deletableFile(f)))) return
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allFiles = sets.flatMap((s) => s.files)
  const selectedFiles = allFiles.filter((f) => selected.has(f.id))
  const selectedBytes = selectedFiles.reduce((s, f) => s + f.bytes, 0)

  const handleDeleteSelected = async () => {
    if (!selectedFiles.length) return
    const ok = await confirm({
      title: t('settings.storage.manage.deleteSelected'),
      message: t('settings.storage.manage.deleteSelectedConfirm', {
        count: selectedFiles.length,
        size: formatBytes(selectedBytes),
      }),
      type: 'danger',
      confirmText: t('common:delete'),
    })
    if (!ok) return

    setDeleting(true)
    // #1316 review round 2 (owner, 2026-10-28): the bulk delete the person confirmed FINISHES as the identity that
    // confirmed it — every delete goes as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    const succeeded = new Set<string>()
    const failed: string[] = []
    for (const f of selectedFiles) {
      try {
        // Inline, not the delete job: these are single files, never a big tree (#921).
        await filesApi.deleteFile(f.id, batch.opts())
        succeeded.add(f.id)
      } catch {
        failed.push(f.name)
      }
    }
    if (batch.ended()) return
    setSelected(new Set())
    setError(failed.length ? t('settings.storage.manage.deleteFailed', { names: failed.join(', ') }) : null)
    setDeleting(false)
    if (succeeded.size) {
      onChanged()
      // Sets change shape after deletions (a 2-copy set stops being a
      // set) — reload rather than splicing.
      load()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className={`relative z-10 max-w-lg w-full max-h-[80vh] flex flex-col ${themeClass} ws-glass-modal ws-anim-modal-in`}>
        <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text truncate">{t('settings.storage.duplicates.title')}</h3>
            {data && (
              <p className="themed-text-muted text-xs mt-0.5">
                {t('settings.storage.duplicates.summary', {
                  count: data.totalSets,
                  size: formatBytes(data.totalWastedBytes),
                })}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:close')}
            className="p-1 -m-1 themed-text-muted hover:themed-text transition-colors rounded"
          >
            ✕
          </button>
        </div>

        <div className="px-5 overflow-y-auto min-h-0 flex-1">
          {loading && <div className="py-6 text-sm themed-text-muted">{t('common:loading')}</div>}
          {forbidden && (
            <div className="py-6 text-sm themed-text-muted">{t('settings.storage.manage.noAccess')}</div>
          )}
          {error && <div className="py-2 text-sm text-red-400">{error}</div>}
          {data && !loading && sets.length === 0 && !forbidden && (
            <div className="py-6 text-sm themed-text-muted">{t('settings.storage.duplicates.none')}</div>
          )}

          {sets.map((s) => {
            const isOpen = expandedSet.has(s.hash)
            const allSelected = s.files.every((f) => selected.has(f.id))
            const someSelected = !allSelected && s.files.some((f) => selected.has(f.id))
            return (
              <div key={s.hash} className="border-b themed-border-secondary last:border-b-0">
                <div className="flex items-center gap-2.5 py-2 text-sm">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    // Selecting a whole set deletes EVERY copy — legal, but
                    // the default gesture should keep one: toggle selects the
                    // copies the person may delete, all but the NEWEST (files
                    // arrive newest-first).
                    onChange={() => {
                      setSelected((prev) => {
                        const next = new Set(prev)
                        const anyIn = s.files.some((f) => next.has(f.id))
                        if (anyIn) for (const f of s.files) next.delete(f.id)
                        // Never the NEWEST copy (it stays with the person), and only copies they may delete (#1102 item 4).
                        else for (const f of s.files.slice(1)) if (deletableFile(f)) next.add(f.id)
                        return next
                      })
                    }}
                    aria-label={s.files[0]?.name ?? s.hash}
                  />
                  <button
                    onClick={() => toggleExpanded(s.hash)}
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span className="themed-text-muted text-[10px] w-3 shrink-0">{isOpen ? '▾' : '▸'}</span>
                    <span className="themed-text font-medium truncate group-hover:underline underline-offset-2" title={s.files[0]?.name}>
                      {s.files[0]?.name}
                    </span>
                  </button>
                  <span className="themed-text-muted text-xs tabular-nums shrink-0">
                    {t('settings.storage.duplicates.setInfo', { copies: s.copies, size: formatBytes(s.size) })}
                  </span>
                  <span className="themed-text tabular-nums text-xs shrink-0">{formatBytes(s.wastedBytes)}</span>
                </div>
                {isOpen && s.files.map((f) => {
                  const dir = folderLabel(f.folder)
                  const fullPath = dir === '/' ? `/${f.name}` : `${dir}/${f.name}`
                  return (
                  <label key={f.id} className="flex items-center gap-2.5 py-1.5 pl-6 pr-1 text-sm cursor-pointer hover:themed-bg-tertiary rounded">
                    <Checkbox checked={selected.has(f.id)} onChange={() => toggleFile(f.id)} disabled={!deletableFile(f)} />
                    <span className="themed-text flex-1 min-w-0 truncate" title={fullPath}>
                      {shortenPath(fullPath, 40)}
                    </span>
                    <span className="themed-text-muted text-xs tabular-nums w-14 text-right shrink-0">{fmtDate(f.modifiedAt)}</span>
                    {onOpenFile && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenFile(f.folderId, f.id) }}
                        className="themed-text-muted hover:themed-text text-xs shrink-0 px-0.5"
                        title={t('settings.storage.manage.openInDrive')}
                        aria-label={t('settings.storage.manage.openInDrive')}
                      >
                        ↗
                      </button>
                    )}
                  </label>
                  )
                })}
              </div>
            )
          })}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t themed-border-secondary">
          <span className="themed-text-muted text-xs tabular-nums">
            {t('settings.storage.manage.selectedSummary', {
              count: selected.size,
              size: formatBytes(selectedBytes),
            })}
          </span>
          <button
            onClick={handleDeleteSelected}
            disabled={!selected.size || deleting}
            className="text-sm px-3 py-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {deleting ? t('common:loading') : t('settings.storage.manage.deleteSelected')}
          </button>
        </div>
      </div>
    </div>
  )
}
